import * as React from 'react'
import {IMapDispatcher, IGeometryEditsDispatcher} from '../../interfaces/dispatchers'
import {getMapService} from '../../reducers/map'
import {DrawType} from '../../enums/DrawType'

import {Feature, Geometry, Point, Polygon, LineString, MultiPolygon, MultiLineString} from '../../models'
import {GeometryType} from '../../enums/GeometryType'
import {DrawSource} from '../../enums/DrawSource'
import {CoordinateSystem} from '../../enums/CoordinateSystem'
import {loadavg} from 'os'
import OverlayLoader from '../Loading/OverlayLoading'
import {config} from '../../reducers/config'
import {nls} from '../nls'
import {ServiceHub} from '../../ServiceHub'
import {CsSelector} from '../Controls/CsSelector'
import {GeometryStringConverter} from './GeometryStringConverter'

interface Props {
  geometryType: GeometryType
  buttonsClassname?: string
  feature: Feature
  defaultDisplayCs: CoordinateSystem
  showTransformSelector?: boolean
  onGeometryChange?: (g?: Geometry) => void
  hideMessages?: boolean
  info?: any
}

interface State {
  geometryString: string
  displayCS: CoordinateSystem
  errors?: string[]
  loading: boolean
  rowEditorMode: boolean
}

class GeometryEditor extends React.Component<Props, State> {
  protected geometryStringConverter: GeometryStringConverter
  protected partStringSeparator: string = '#'
  protected holeStringSeparator: string = '$'
  constructor(props: Props) {
    super(props)
    this.geometryStringConverter = new GeometryStringConverter(this.partStringSeparator, this.holeStringSeparator)
    this.activateSinglePartGeometrySetter = this.activateSinglePartGeometrySetter.bind(this)
    this.state = {
      displayCS: props.defaultDisplayCs,
      geometryString: '',
      loading: true,
      rowEditorMode: false
    }
  }
  typingTimeout: ReturnType<typeof setTimeout>
  unmounted = false
  componentWillUnmount() {
    this.unmounted = true
  }

  componentDidMount() {
    this.loadDependencies(this.props)
  }

  componentDidUpdate(prevProps: Props) {
    if (prevProps.feature !== this.props.feature) {
      this.loadDependencies(this.props)
    }
  }
  lastReceivedString = ''
  loadDependencies(props: Props) {
    const newString =
      props.feature.getGeometry() == null
        ? ''
        : this.geometryStringConverter.getGeometryAsString(
            (this.state && this.state.displayCS) || this.props.defaultDisplayCs,
            props.feature.getGeometry().getType(),
            props.feature.getGeometry()
          )
    if (newString != this.lastReceivedString || !this.state.geometryString) {
      this.lastReceivedString = newString
      this.setState({...this.state, loading: true}, () => {
        this.geometryStringConverter
          .getGeometryAsStringAsync(
            (this.state && this.state.displayCS) || this.props.defaultDisplayCs,
            props.geometryType,
            props.feature.getGeometry()
          )
          .then(geometryString => {
            if (!this.unmounted) {
              this.setState({...this.state, geometryString: geometryString, loading: false})
            }
          })
      })
    }
  }

  mapGeometryTypeToDraw(geomType: GeometryType): DrawType {
    if (geomType == GeometryType.LineString || geomType == GeometryType.MultiLineString) return DrawType.LineString
    if (geomType == GeometryType.Polygon || geomType == GeometryType.MultiPolygon) return DrawType.Polygon
    if (geomType == GeometryType.Point) return DrawType.Point
    return DrawType.Point
  }

  activateSinglePartGeometrySetter() {
    if (
      this.props.geometryType == GeometryType.Point ||
      this.props.geometryType == GeometryType.Polygon ||
      this.props.geometryType == GeometryType.LineString
    ) {
      getMapService().drawing.activateDraw(
        this.mapGeometryTypeToDraw(this.props.geometryType),
        DrawSource.Edit,
        () => {},
        f => {
          getMapService().drawing.deactivateDraw(true)
          this.geometryStringConverter
            .getGeometryAsStringAsync(
              (this.state && this.state.displayCS) || this.props.defaultDisplayCs,
              this.props.geometryType,
              f.getGeometry()
            )
            .then(geometryString => {
              this.setNewGeometryString(geometryString, f.getGeometry())
            })
        }
      )
    } else {
      throw new Error(`${this.props.geometryType} not implemented`)
    }
  }

  activatePartGeometrySetter(partIndex: number, holeIndex: number, isHole: boolean) {
    if (this.props.geometryType == GeometryType.MultiLineString) {
      throw new Error(`${this.props.geometryType} not implemented`) //TODO
    }
    const getPartType = (wholeType: GeometryType): GeometryType => {
      if (this.props.geometryType == GeometryType.MultiPolygon || this.props.geometryType == GeometryType.Polygon) {
        return GeometryType.Polygon
      } else if (
        this.props.geometryType == GeometryType.LineString ||
        this.props.geometryType == GeometryType.MultiLineString
      ) {
        return GeometryType.LineString
      }
      throw new Error(`${wholeType} not implemented`) //TODO
    }
    const partType = getPartType(this.props.geometryType)
    getMapService().drawing.activateDraw(
      this.mapGeometryTypeToDraw(partType),
      DrawSource.Edit,
      () => {},
      f => {
        getMapService().drawing.deactivateDraw(true)
        this.geometryStringConverter
          .getGeometryAsStringAsync(
            (this.state && this.state.displayCS) || this.props.defaultDisplayCs,
            partType,
            f.getGeometry()
          )
          .then(geometryString => {
            const geomStrParts = this.state.geometryString.split(this.partStringSeparator)
            if (isHole) {
              const holes = geomStrParts[partIndex].split(this.holeStringSeparator)
              holes[holeIndex] = geometryString
              geometryString = holes.join(this.holeStringSeparator)
            }
            geomStrParts[partIndex] = geometryString
            this.setNewGeometryString(geomStrParts.join(this.partStringSeparator))
          })
      }
    )
  }

  geometrySetTimeout: ReturnType<typeof setTimeout>

  getCsLabel(cs: CoordinateSystem) {
    switch (cs) {
      case CoordinateSystem.GEOGRAPHIC:
        return nls.transformations.GEOGRAPHIC
      case CoordinateSystem.WEB_MERCATOR:
        return nls.transformations.WEB_MERCATOR
      case CoordinateSystem.BGS2005:
        return nls.transformations.BGS2005
      case CoordinateSystem.BG_703:
        return nls.transformations.BG_703
      case CoordinateSystem.BG_705:
        return nls.transformations.BG_705
      case CoordinateSystem.BG_707:
        return nls.transformations.BG_707
      case CoordinateSystem.BG_709:
        return nls.transformations.BG_709
    }
  }

  getProjectionSelector(): JSX.Element {
    return (
      <CsSelector
        className=""
        selected={this.state.displayCS}
        onChange={s => {
          this.setState({...this.state, displayCS: s, loading: true})
          setTimeout(() => {
            this.geometryStringConverter
              .getGeometryAsStringAsync(
                (this.state && this.state.displayCS) || this.props.defaultDisplayCs,
                this.props.geometryType,
                this.props.feature.getGeometry()
              )
              .then(geometryString => {
                this.setState({...this.state, geometryString, loading: false})
              })
          }, 200)
        }}
      />
    )
  }

  getCoordinatesMessage(): JSX.Element {
    if (this.props.hideMessages) return <div></div>
    return (
      <React.Fragment>
        <br></br>
        <div style={{display: 'flex', margin: '5px', alignItems: 'center', justifyContent: 'space-between'}}>
          <div>
            <span>Напишете </span>
            <span>
              {this.props.showTransformSelector ? this.getProjectionSelector() : this.getCsLabel(this.state.displayCS)}
            </span>
            <span> координати</span>
          </div>
          {this.props.geometryType == GeometryType.Point ? null : (
            <button
              type="button"
              className="button"
              onClick={() => {
                const errors = this.geometryStringConverter.getGeomStringErrors(
                  this.props.geometryType,
                  this.state.geometryString
                )
                if (errors.length == 0) {
                  this.setState({...this.state, rowEditorMode: !this.state.rowEditorMode})
                } else {
                  this.setState({...this.state, errors: errors})
                }
              }}
            >
              {this.state.rowEditorMode ? 'Покажи текст' : 'Покажи редове'}
            </button>
          )}
        </div>
      </React.Fragment>
    )
  }

  setNewGeometryString(geometryString: string, geometry?: Geometry): void {
    const errors = this.geometryStringConverter.getGeomStringErrors(this.props.geometryType, geometryString)
    this.setState({...this.state, errors, geometryString, loading: errors.length == 0 && !geometry}, () => {
      if (errors.length == 0) {
        if (!geometry) {
          this.geometryStringConverter
            .tryGetGeometryFromString(this.state.displayCS, this.props.geometryType, geometryString)
            .then(geom => {
              this.setState({...this.state, loading: false})
              this._setNewGeometryString(geometryString, geom)
            })
        } else {
          this._setNewGeometryString(geometryString, geometry)
        }
      }
    })
  }

  protected _setNewGeometryString(geometryString: string, geometry?: Geometry) {
    this.props.feature.setGeometry(geometry)
    if (this.props.onGeometryChange) {
      this.props.onGeometryChange(geometry)
    }
  }

  showOnMapButton(): JSX.Element {
    return (
      <button
        type="button"
        className={this.props.buttonsClassname || 'button'}
        style={{}}
        onClick={() => {
          if (this.props.feature && this.props.feature.getGeometry()) {
            getMapService().panTo(this.props.feature.getGeometry())
          }
        }}
      >
        Покажи на картата
      </button>
    )
  }

  protected getPointGeometryEditingTools(feature: Feature): JSX.Element {
    let xValue = ''
    let yValue = ''
    if (this.state.geometryString) {
      xValue = this.state.geometryString.split(';')[1]
      yValue = this.state.geometryString.split(';')[0]
    }
    return (
      <div style={{display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between'}}>
        <input
          className="form-control"
          style={{margin: '0 2px', minWidth: '130px', width: '0%', flex: '1 auto'}}
          value={yValue}
          type="number"
          placeholder="Пример: 42.968211"
          onChange={e => {
            const v = e.target.value
            this.setState({...this.state, geometryString: v})
            clearTimeout(this.typingTimeout)
            this.typingTimeout = setTimeout(() => {
              this.setNewGeometryString(`${v};${xValue}`)
            }, 500)
          }}
        />
        <input
          className="form-control"
          style={{margin: '0 2px', minWidth: '130px', width: '0%', flex: '1 auto'}}
          value={xValue}
          type="number"
          placeholder="Пример: 23.561235"
          onChange={e => {
            const v = e.target.value
            this.setState({...this.state, geometryString: v})
            clearTimeout(this.typingTimeout)
            this.typingTimeout = setTimeout(() => {
              this.setNewGeometryString(`${yValue};${v}`)
            }, 500)
          }}
        />
        {this.showOnMapButton()}
      </div>
    )
  }

  protected getShapeTools(partIndex: number, holeIndex: number, isHole: boolean) {
    const buttonStyle: React.CSSProperties = {width: '40px', margin: '0px 10px'}
    return (
      <div
        style={{display: 'flex', position: 'absolute', right: '0px', bottom: '10px', justifyContent: 'space-between'}}
      >
        {!this.isSinglePart() && !isHole ? (
          <button
            type="button"
            style={buttonStyle}
            className="button"
            title="Добави част"
            onClick={() => {
              const geomStrParts = this.state.geometryString.split(this.partStringSeparator)
              geomStrParts[partIndex] = geomStrParts[partIndex] + this.partStringSeparator
              this.setNewGeometryString(geomStrParts.join(this.partStringSeparator))
            }}
          >
            <i className="fas fa-plus" />
          </button>
        ) : null}
        {this.canHaveHoles() && !isHole ? (
          <button
            type="button"
            style={buttonStyle}
            className="button"
            title="Добави дупка"
            onClick={() => {
              const geomStrParts = this.state.geometryString.split(this.partStringSeparator)
              geomStrParts[partIndex] = geomStrParts[partIndex] + this.holeStringSeparator
              this.setNewGeometryString(geomStrParts.join(this.partStringSeparator))
            }}
          >
            <i className="fas fa-plus-square" />
          </button>
        ) : null}
        <button
          type="button"
          style={buttonStyle}
          className="button"
          title="Изчертай на картата"
          onClick={() => this.activatePartGeometrySetter(partIndex, holeIndex, isHole)}
        >
          <i className="fas fa-crosshairs" />
        </button>

        {partIndex != 0 || isHole ? (
          <button
            type="button"
            style={buttonStyle}
            className="button"
            title="Премахни тази част"
            onClick={() => {
              const geomStrParts = this.state.geometryString.split(this.partStringSeparator)
              if (isHole) {
                const holes = geomStrParts[partIndex].split(this.holeStringSeparator)
                holes.splice(holeIndex, 1)
                geomStrParts[partIndex] = holes.join(this.holeStringSeparator)
              } else {
                geomStrParts.splice(partIndex, 1)
              }
              this.setNewGeometryString(geomStrParts.join(this.partStringSeparator))
            }}
          >
            <i className="fas fa-trash" />
          </button>
        ) : null}
        <button
          type="button"
          style={buttonStyle}
          className="button"
          title="Покажи на картата"
          onClick={() => {
            let geomstr = this.state.geometryString.split(this.partStringSeparator)[partIndex]
            if (isHole) {
              geomstr = geomstr.split(this.holeStringSeparator)[holeIndex]
            } else if (this.canHaveHoles()) {
              geomstr = geomstr.split(this.holeStringSeparator)[0]
            }
            this.geometryStringConverter
              .tryGetGeometryFromString(this.state.displayCS, this.props.geometryType, geomstr)
              .then(geom => {
                if (geom) {
                  getMapService()
                    .panTo(geom)
                    .then(() => {
                      getMapService().drawing.flash([geom])
                    })
                }
              })
          }}
        >
          <i className="fas fa-map-marker-alt" />
        </button>
        <button
          type="button"
          style={buttonStyle}
          className="button approve"
          title="Приложи промените"
          onClick={() => this.setNewGeometryString(this.state.geometryString)}
        >
          <i className="fas fa-check" />
        </button>
      </div>
    )
  }

  protected canHaveHoles(): boolean {
    return this.props.geometryType == GeometryType.Polygon || this.props.geometryType == GeometryType.MultiPolygon
  }

  getTextEditor(
    hole: string,
    isHole: boolean,
    holeStrings: string[],
    holeIndex: number,
    partIndex: number,
    parts: string[]
  ) {
    return (
      <textarea
        style={
          {
            width: '100%',
            minWidth: '400px',
            minHeight: '300px',
            resize: 'vertical',
            paddingBottom: '15px'
          } as React.CSSProperties
        }
        className="form-control"
        value={hole}
        placeholder={isHole ? 'Коориднати на дупката' : 'Попълнете координати за тази част от геометрията'}
        onChange={e => {
          let val = e.target.value
          holeStrings[holeIndex] = val
          val = holeStrings.join(this.holeStringSeparator)
          parts[partIndex] = val
          this.setState({...this.state, geometryString: parts.join(this.partStringSeparator)})
        }}
      ></textarea>
    )
  }

  getCoordinateRowEditor(value: string[], onChange: (change?: string) => void, key: string) {
    const getCoord = (): Promise<Point> => {
      return new Promise((res, rej) => {
        const x = parseFloat(value[0])
        const y = parseFloat(value[1])
        if (!isNaN(x) && !isNaN(y)) {
          const point = new Point([y, x])
          point.cs = this.state.displayCS
          ServiceHub.BgTransService.bgTrans(CoordinateSystem.WEB_MERCATOR, point).then(geom => {
            res(geom.getCenter())
          })
        } else {
          rej()
        }
      })
    }
    return (
      <div
        key={key}
        style={{display: 'flex'}}
        className="coord-row-editor"
        onMouseEnter={() => {
          getMapService().drawing.unMark(DrawSource.Select)
          getCoord().then(geom => {
            const feat = new Feature()
            feat.setGeometry(geom)
            getMapService().drawing.mark([feat], DrawSource.Select)
          })
        }}
      >
        <input
          type="number"
          className="form-control"
          value={value[0]}
          onChange={e => {
            onChange(`${e.target.value} ${value[1]}`)
          }}
        ></input>
        <input
          type="number"
          className="form-control"
          value={value[1]}
          onChange={e => {
            onChange(`${value[0]} ${e.target.value}`)
          }}
        ></input>
        <span
          className="table-row-tool"
          onClick={() => getCoord().then(geom => getMapService().zoomTo([geom]))}
          style={{
            cursor: 'pointer',
            fontSize: '26px',
            color: 'rgb(64, 122, 171)',
            textAlign: 'center',
            minWidth: '50px'
          }}
        >
          <i className="fas fa-map-marker-alt" />
        </span>
        <span
          className="table-row-tool"
          onClick={() => onChange(undefined)}
          style={{
            cursor: 'pointer',
            fontSize: '26px',
            color: 'rgb(64, 122, 171)',
            textAlign: 'center',
            minWidth: '50px'
          }}
        >
          <i className="fas fa-trash" />
        </span>
      </div>
    )
  }

  getRowEditor(
    hole: string,
    isHole: boolean,
    holeStrings: string[],
    holeIndex: number,
    partIndex: number,
    parts: string[]
  ) {
    const rows: JSX.Element[] = []
    const points = hole.split('\n')
    for (let i = 0; i < points.length; i++) {
      const p = points[i] || ''

      let xy = p.replace(/\t/gi, ' ').replace(/,/gi, ' ').replace(/\s\s+/g, ' ').trim().split(' ')
      if (xy.length < 1) {
        xy.push('')
        xy.push('')
      }
      if (xy.length < 2) {
        xy.push('')
      }
      xy = [xy[0], xy[1]]

      rows.push(
        this.getCoordinateRowEditor(
          xy,
          (change?: string) => {
            const newPoints = points.map(n => n)
            if (change === undefined) {
              newPoints.splice(i, 1)
            } else {
              newPoints[i] = change
            }

            holeStrings[holeIndex] = newPoints.join('\n')
            const val = holeStrings.join(this.holeStringSeparator)
            parts[partIndex] = val
            this.setState({...this.state, geometryString: parts.join(this.partStringSeparator)}, () => {
              if (change === undefined) {
                this.setNewGeometryString(this.state.geometryString)
              }
            })
          },
          `${holeIndex}${partIndex}${i}`
        )
      )
    }
    return (
      <div style={{minHeight: '100px', padding: '10px 0 45px 0'}}>
        {rows}
        <button
          type="button"
          className="button"
          onClick={() => {
            points.push('')
            holeStrings[holeIndex] = points.join('\n')
            const val = holeStrings.join(this.holeStringSeparator)
            parts[partIndex] = val
            this.setState({...this.state, geometryString: parts.join(this.partStringSeparator)})
          }}
        >
          Добави ред
        </button>
      </div>
    )
  }

  protected getSimpleShapeEditingTools(partIndex: number, parts: string[]) {
    let holeStrings = [parts[partIndex]]
    if (this.canHaveHoles()) {
      holeStrings = parts[partIndex].split(this.holeStringSeparator)
    }
    const result: JSX.Element[] = []
    holeStrings.forEach((hole, holeIndex) => {
      const isHole = holeIndex > 0
      result.push(
        <div
          key={partIndex.toString() + ':' + holeIndex.toString()}
          style={{position: 'relative', margin: isHole ? '5px 0px 5px 30px' : ''}}
        >
          <div>{isHole ? `Дупка № ${holeIndex}` : parts.length > 1 ? `Част № ${partIndex + 1}` : ''}</div>
          {this.state.rowEditorMode
            ? this.getRowEditor(hole, isHole, holeStrings, holeIndex, partIndex, parts)
            : this.getTextEditor(hole, isHole, holeStrings, holeIndex, partIndex, parts)}
          {this.getShapeTools(partIndex, holeIndex, isHole)}
        </div>
      )
    })
    return <div key={partIndex}>{result}</div>
  }

  protected getMultiShapeEditingTools() {
    const parts = this.state.geometryString.split(this.partStringSeparator)
    const result: JSX.Element[] = []
    parts.forEach((p, i) => {
      result.push(this.getSimpleShapeEditingTools(i, parts))
    })
    return result
  }
  isSinglePart(): boolean {
    return (
      this.props.geometryType == GeometryType.Polygon ||
      this.props.geometryType == GeometryType.LineString ||
      this.props.geometryType == GeometryType.Point
    )
  }

  protected getShapeEditingTools(): JSX.Element {
    if (this.props.geometryType == GeometryType.MultiLineString) {
      throw new Error('not impl')
    }
    return (
      <div style={{display: 'flex', flexDirection: 'column'}}>
        {this.isSinglePart()
          ? this.getSimpleShapeEditingTools(0, [this.state.geometryString])
          : this.getMultiShapeEditingTools()}
      </div>
    )
  }

  protected getGeometryEditingTools(feature: Feature): JSX.Element {
    let content = <div></div>
    if (!this.props.geometryType) {
      return content
    }
    if (this.props.geometryType == GeometryType.Point) {
      content = this.getPointGeometryEditingTools(feature)
    } else if (
      this.props.geometryType == GeometryType.Polygon ||
      this.props.geometryType == GeometryType.LineString ||
      this.props.geometryType == GeometryType.MultiPolygon ||
      this.props.geometryType == GeometryType.MultiLineString
    ) {
      content = this.getShapeEditingTools()
    } else {
      throw new Error(`Editing "${this.props.geometryType}" not implemented yet`)
    }
    return (
      <div style={{position: 'relative'}}>
        <OverlayLoader show={this.state.loading}></OverlayLoader>

        {this.getCoordinatesMessage()}
        {content}
        {this.props.geometryType == GeometryType.Point ? this.getGeometrySettingControl() : null}
        {this.state.errors
          ? this.state.errors.map((c, i) => (
              <div className="error-div" key={i}>
                {c}
              </div>
            ))
          : null}
      </div>
    )
  }

  protected getGeometrySettingControl(): JSX.Element {
    return (
      <React.Fragment>
        <br />
        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
          <div>Или посочете на картата</div>
          <button
            type="button"
            style={{width: '40px'}}
            className={this.props.buttonsClassname || 'button width'}
            title="Посочете на картата"
            onClick={() => this.activateSinglePartGeometrySetter()}
          >
            <i className="fas fa-crosshairs" />
          </button>
        </div>
        <br />
      </React.Fragment>
    )
  }

  render() {
    return this.getGeometryEditingTools(this.props.feature)
  }
}

export default GeometryEditor
